import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { COUNTRIES, COUNTRY_BY_CODE, searchCountries } from '../../data/countries';

/**
 * PhoneInput
 * --------------
 * A country-code dropdown (with flag emoji + search) glued to a phone-number field.
 *
 * Props:
 *   value         — full E.164 phone string (e.g. "+966512345678")
 *   onChange(val) — callback returning the new combined value
 *   defaultIso    — initial ISO-2 country code (defaults to SA)
 *   isRTL         — whether to render the popover with Arabic strings
 *   inputClassName, testId — pass-through for the phone number input
 */
export const PhoneInput = ({
  value = '',
  onChange,
  defaultIso = 'SA',
  isRTL = false,
  inputClassName = '',
  testId = 'phone-input',
}) => {
  // Derive initial country/local-number from a possibly pre-filled value.
  const initial = useMemo(() => {
    if (value) {
      const match = COUNTRIES.find(c => value.startsWith(c.dial));
      if (match) {
        return { iso: match.code, local: value.slice(match.dial.length).trim() };
      }
    }
    return { iso: defaultIso, local: '' };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [iso, setIso] = useState(initial.iso);
  const [local, setLocal] = useState(initial.local);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrapRef = useRef(null);

  const country = COUNTRY_BY_CODE[iso] || COUNTRY_BY_CODE[defaultIso];

  // Emit combined value upward
  useEffect(() => {
    const cleaned = local.replace(/[^\d]/g, '');
    onChange?.(cleaned ? `${country.dial}${cleaned}` : '');
  }, [iso, local]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close popover when clicking outside
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const results = useMemo(() => searchCountries(q), [q]);

  return (
    <div ref={wrapRef} className={`relative flex items-stretch ${isRTL ? 'flex-row-reverse' : ''}`} data-testid={`${testId}-wrap`}>
      {/* Country code selector */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`h-12 px-3 inline-flex items-center gap-2 bg-white border-2 border-[rgba(20,15,10,0.10)] hover:border-flame-400 transition-colors ${isRTL ? 'rounded-e-xl border-s-0' : 'rounded-s-xl border-e-0'}`}
        data-testid={`${testId}-country-button`}
      >
        <span className="text-[18px] leading-none">{country.flag}</span>
        <span className="text-[13px] font-semibold text-ink-900 tabular-nums">{country.dial}</span>
        <ChevronDown className="w-3.5 h-3.5 text-ink-500" />
      </button>

      {/* Phone number input */}
      <input
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        placeholder={isRTL ? 'رقم الهاتف' : 'Phone number'}
        dir="ltr"
        className={`flex-1 h-12 px-4 bg-white border-2 border-[rgba(20,15,10,0.10)] text-ink-900 placeholder:text-ink-400 focus:border-flame-500 focus:ring-4 focus:ring-flame-500/15 transition-all ${isRTL ? 'rounded-s-xl text-right' : 'rounded-e-xl'} ${inputClassName}`}
        data-testid={testId}
      />

      {/* Dropdown — opens UPWARDS with VAROTRADES design */}
      {open && (
        <div
          className={`absolute bottom-full mb-2 z-[60] w-[360px] max-h-[380px] bg-white border border-[rgba(20,15,10,0.10)] rounded-2xl shadow-[0_24px_60px_-12px_rgba(20,15,10,0.25)] overflow-hidden flex flex-col ${isRTL ? 'right-0' : 'left-0'}`}
          data-testid={`${testId}-popover`}
        >
          {/* Brand header */}
          <div className="px-4 pt-3.5 pb-2.5 border-b border-[rgba(20,15,10,0.06)] bg-gradient-to-br from-[#FFF6EC] to-white">
            <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-flame-700 mb-1">
              {isRTL ? 'اختر الدولة' : 'Select country'}
            </div>
            <div className="relative">
              <Search className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 ${isRTL ? 'right-3' : 'left-3'}`} />
              <input
                autoFocus
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder={isRTL ? 'ابحث باسم الدولة أو الرمز…' : 'Search by country name or code…'}
                className={`w-full h-10 ${isRTL ? 'pr-9 pl-3 text-right' : 'pl-9 pr-3'} text-[13px] bg-white border border-[rgba(20,15,10,0.08)] rounded-lg focus:outline-none focus:border-flame-500 focus:ring-2 focus:ring-flame-500/15`}
                data-testid={`${testId}-search`}
              />
            </div>
          </div>
          <div
            className="overflow-y-auto flex-1 overscroll-contain"
            onWheel={(e) => { e.stopPropagation(); }}
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {results.length === 0 && (
              <div className="p-8 text-center text-[13px] text-ink-500">
                {isRTL ? 'لا توجد نتائج مطابقة' : 'No matching results'}
              </div>
            )}
            {results.map(c => (
              <button
                key={c.code}
                type="button"
                onClick={() => { setIso(c.code); setOpen(false); setQ(''); }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-[13px] hover:bg-[#FFF6EC] transition-colors ${isRTL ? 'flex-row-reverse text-right' : ''} ${iso === c.code ? 'bg-[#FFF6EC] border-s-2 border-flame-500' : 'border-s-2 border-transparent'}`}
                data-testid={`${testId}-option-${c.code}`}
              >
                <span className="text-[22px] leading-none">{c.flag}</span>
                <span className="flex-1 min-w-0">
                  <span className="block text-ink-900 font-semibold truncate">{isRTL ? c.ar : c.name}</span>
                </span>
                <span className="text-ink-500 tabular-nums font-mono text-[12.5px]">{c.dial}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhoneInput;
